import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
  Menu,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon,
  ShowChart as ShowChartIcon,
  ScatterPlot as ScatterIcon,
  GridOn as GridIcon,
  Palette as PaletteIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, AreaChart, Area, ComposedChart } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import * as XLSX from 'xlsx';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { lt } from 'date-fns/locale';

// Sample CSV data structure
interface CSVData {
  headers: string[];
  rows: any[];
}

  // Report types
  type ReportType = 'family_tickets_weekend' | 'visitor_count' | 'sector_distribution' | 'daily_attendance' | 'seller_performance' | 'family_tickets_weekend_table' | 'visitor_count_table' | 'savaitgaliai' | 'savaitgaliai_table' | 'family_tickets_time_comparison' | 'coupon_sales_daily';

  // Report configuration
  interface ReportConfig {
    id: string;
    type: ReportType;
    title: string;
    description: string;
    data: any[] | { weekendData: { time: string; count: number }[]; allData: { time: string; count: number }[]; weekendOnly: boolean };
    summary?: string;
  }

const Playground: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  
  // State management
  const [csvData, setCsvData] = useState<CSVData | null>(null);
  const [reports, setReports] = useState<ReportConfig[]>([]);
  const [selectedReportType, setSelectedReportType] = useState<ReportType>('family_tickets_weekend');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [presetMenuAnchor, setPresetMenuAnchor] = useState<null | HTMLElement>(null);
  const [weekendOnly, setWeekendOnly] = useState<boolean>(false);

  // Date preset helper functions
  const getDatePresets = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Get start of week (Monday)
    const getStartOfWeek = (date: Date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
      return new Date(d.setDate(diff));
    };
    
    // Get end of week (Sunday)
    const getEndOfWeek = (date: Date) => {
      const start = getStartOfWeek(date);
      return new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
    };
    
    // Get start of month
    const getStartOfMonth = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    };
    
    // Get end of month
    const getEndOfMonth = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    };
    
    // Get start of quarter
    const getStartOfQuarter = (date: Date) => {
      const quarter = Math.floor(date.getMonth() / 3);
      return new Date(date.getFullYear(), quarter * 3, 1);
    };
    
    // Get end of quarter
    const getEndOfQuarter = (date: Date) => {
      const quarter = Math.floor(date.getMonth() / 3);
      return new Date(date.getFullYear(), (quarter + 1) * 3, 0);
    };
    
    // Get start of year
    const getStartOfYear = (date: Date) => {
      return new Date(date.getFullYear(), 0, 1);
    };
    
    // Get end of year
    const getEndOfYear = (date: Date) => {
      return new Date(date.getFullYear(), 11, 31);
    };
    
    const thisWeekStart = getStartOfWeek(today);
    const thisWeekEnd = getEndOfWeek(today);
    
    const lastWeekStart = new Date(thisWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastWeekEnd = new Date(thisWeekEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const thisMonthStart = getStartOfMonth(today);
    const thisMonthEnd = getEndOfMonth(today);
    
    const lastMonthStart = getStartOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1));
    const lastMonthEnd = getEndOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1));
    
    const thisQuarterStart = getStartOfQuarter(today);
    const thisQuarterEnd = getEndOfQuarter(today);
    
    const lastQuarterStart = getStartOfQuarter(new Date(today.getFullYear(), today.getMonth() - 3, 1));
    const lastQuarterEnd = getEndOfQuarter(new Date(today.getFullYear(), today.getMonth() - 3, 1));
    
    const thisYearStart = getStartOfYear(today);
    const thisYearEnd = getEndOfYear(today);
    
    const lastYearStart = getStartOfYear(new Date(today.getFullYear() - 1, 0, 1));
    const lastYearEnd = getEndOfYear(new Date(today.getFullYear() - 1, 0, 1));
    
    return {
      thisWeek: { start: thisWeekStart, end: thisWeekEnd },
      lastWeek: { start: lastWeekStart, end: lastWeekEnd },
      thisMonth: { start: thisMonthStart, end: thisMonthEnd },
      lastMonth: { start: lastMonthStart, end: lastMonthEnd },
      thisQuarter: { start: thisQuarterStart, end: thisQuarterEnd },
      lastQuarter: { start: lastQuarterStart, end: lastQuarterEnd },
      thisYear: { start: thisYearStart, end: thisYearEnd },
      lastYear: { start: lastYearStart, end: lastYearEnd }
    };
  };
  
  const handlePresetClick = (event: React.MouseEvent<HTMLElement>) => {
    setPresetMenuAnchor(event.currentTarget);
  };
  
  const handlePresetClose = () => {
    setPresetMenuAnchor(null);
  };
  
  const applyPreset = (preset: 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'lastQuarter' | 'thisYear' | 'lastYear') => {
    const presets = getDatePresets();
    const selected = presets[preset];
    setStartDate(selected.start);
    setEndDate(selected.end);
    handlePresetClose();
  };

  // Color palette for charts
  const colorPalette = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
    '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
  ];

  // Check if user is admin (for testing - change back to master admin in production)
  const isMasterAdmin = hasPermission('*.*') || hasPermission('admin.panel_visibility');

  // Handle CSV and Excel file upload
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);

    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    const isCsv = fileName.endsWith('.csv');

    if (!isExcel && !isCsv) {
      setError('Unsupported file format. Please upload a CSV or Excel file (.csv, .xlsx, .xls).');
      setIsLoading(false);
      return;
    }

    const reader = new FileReader();

    if (isExcel) {
      // Handle Excel files
      reader.onload = (e) => {
        try {
          const data = e.target?.result as ArrayBuffer;
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON with header row
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as any[][];
          
          if (jsonData.length === 0) {
            setError('Excel file is empty or invalid.');
            setIsLoading(false);
            return;
          }

          // First row is headers
          const headers = jsonData[0].map((h: any) => String(h).trim());
          
          // Remaining rows are data
          const rows = jsonData.slice(1)
            .filter(row => row.some(cell => cell !== ''))
            .map(row => {
              const rowObj: any = {};
              headers.forEach((header, index) => {
                rowObj[header] = row[index] !== undefined ? String(row[index]).trim() : '';
              });
              return rowObj;
            });

          setCsvData({ headers, rows });
          setIsLoading(false);
        } catch (err) {
          setError('Failed to parse Excel file. Please check the format.');
          setIsLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Handle CSV files (existing logic)
      reader.onload = (e) => {
        try {
          const csv = e.target?.result as string;
          const lines = csv.split('\n');
          
          // Handle both comma and semicolon delimiters
          const firstLine = lines[0];
          const delimiter = firstLine.includes(';') ? ';' : ',';
          
          const headers = firstLine.split(delimiter).map(h => h.trim().replace(/"/g, ''));
          const rows = lines.slice(1)
            .filter(line => line.trim())
            .map(line => {
              const values = line.split(delimiter).map(v => v.trim().replace(/"/g, ''));
              const row: any = {};
              headers.forEach((header, index) => {
                row[header] = values[index];
              });
              return row;
            });

          setCsvData({ headers, rows });
          setIsLoading(false);
        } catch (err) {
          setError('Failed to parse CSV file. Please check the format.');
          setIsLoading(false);
        }
      };
      reader.readAsText(file);
    }
  }, []);

  // Helper to parse DD/MM/YYYY as Date
  function parseDDMMYYYY(dateStr: string): Date | null {
    if (!dateStr) return null;
    const [dd, mm, yyyy] = dateStr.split('/');
    if (!dd || !mm || !yyyy) return null;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  }

  // Helper to parse DD/MM/YYYY HH:MM:SS as Date (extracts date portion only)
  function parseDDMMYYYYHHMMSS(dateTimeStr: string): Date | null {
    if (!dateTimeStr) return null;
    // Extract date portion (before space or first space-separated part)
    const datePart = dateTimeStr.split(' ')[0];
    return parseDDMMYYYY(datePart);
  }

  // Helper to format date as "Month-Day" (e.g., "Jan 15") or "Month-Day-Year" (e.g., "Jan 15 2024")
  function formatMonthDay(date: Date, includeYear: boolean = false): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const base = `${months[date.getMonth()]} ${date.getDate()}`;
    return includeYear ? `${base} ${date.getFullYear()}` : base;
  }

  // Helper to get date from row
  function getRowDate(row: any): Date | null {
    const dateStr = row['Renginio data'] || '';
    return parseDDMMYYYY(dateStr);
  }

  // Helper to check if date is in range (inclusive)
  function isInRange(dateObj: Date | null, startDate: Date | null, endDate: Date | null): boolean {
    if (!dateObj) return false;
    if (startDate) {
      // Set startDate to start of day for inclusive comparison
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);
      const dateObjStart = new Date(dateObj);
      dateObjStart.setHours(0, 0, 0, 0);
      if (dateObjStart < startOfDay) return false;
    }
    if (endDate) {
      // Set endDate to end of day for inclusive comparison
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      if (dateObj > endOfDay) return false;
    }
    return true;
  }

  // Helper to filter rows by date range
  function filterRowsByDate(rows: any[], startDate: Date | null, endDate: Date | null): any[] {
    if (!startDate && !endDate) return rows; // No filter = return all
    return rows.filter(row => {
      const dateObj = getRowDate(row);
      return isInRange(dateObj, startDate, endDate);
    });
  }

  function normalizeTextForMatch(input: unknown): string {
    const raw = String(input ?? '').toLowerCase();
    const noDiacritics = typeof (raw as any).normalize === 'function'
      ? raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      : raw;
    return noDiacritics
      .replace(/[.]/g, '') // treat trailing dots as equivalent
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isFamilyTicketPreke(prekeRaw: unknown): boolean {
    const s = normalizeTextForMatch(prekeRaw);
    // support both correct "šeimos" (=> "seimos") and common missing-char variant ("eimos")
    return s.includes('seimos bilietas') || s.includes('eimos bilietas');
  }

  function getRowValue(row: any, keys: string[]): string {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
    }
    return '';
  }

  // Generate report data
  const generateReport = useCallback((reportType: ReportType, dateStart: Date | null = null, dateEnd: Date | null = null, weekendOnly: boolean = false) => {
    if (!csvData) return null;

    // Apply date range filter to all rows first
    const filteredRows = filterRowsByDate(csvData.rows, dateStart, dateEnd);

    switch (reportType) {
      case 'family_tickets_weekend': {
        // Filter for "Ekspozicijos Šeimos Bilietas" 
        const familyTickets = filteredRows.filter(row => {
          const preke = getRowValue(row, ['Prekė', 'Preke']);
          return isFamilyTicketPreke(preke);
        });

        // Group by event time and separate weekdays vs weekends
        const timeGroups: { [key: string]: { weekday: number; weekend: number } } = {};
        
        familyTickets.forEach(ticket => {
          const renginioLaikas = ticket['Renginio laikas'] || '';
          const renginioData = ticket['Renginio data'] || '';
          
          if (!renginioLaikas || !renginioData) return;
          
          const dateObj = parseDDMMYYYY(renginioData);
          if (!dateObj) return;
          
          const dayOfWeek = dateObj.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          // Apply weekend filter if enabled
          if (weekendOnly && !isWeekend) {
            return; // Skip non-weekend tickets when weekendOnly is true
          }
          
          if (!timeGroups[renginioLaikas]) {
            timeGroups[renginioLaikas] = { weekday: 0, weekend: 0 };
          }
          
          if (isWeekend) {
            timeGroups[renginioLaikas].weekend += 1;
          } else {
            timeGroups[renginioLaikas].weekday += 1;
          }
        });

        // Convert to chart data and sort by time
        const chartData = Object.entries(timeGroups)
          .map(([time, counts]) => ({
            time,
            weekday: weekendOnly ? 0 : counts.weekday, // Hide weekday data when weekendOnly is true
            weekend: counts.weekend
          }))
          .sort((a, b) => {
            // Sort by time (assuming HH:MM format)
            const timeA = a.time.split(':').map(Number);
            const timeB = b.time.split(':').map(Number);
            return (timeA[0] * 60 + (timeA[1] || 0)) - (timeB[0] * 60 + (timeB[1] || 0));
          });

        const totalWeekdayTickets = chartData.reduce((sum, item) => sum + item.weekday, 0);
        const totalWeekendTickets = chartData.reduce((sum, item) => sum + item.weekend, 0);

        const description = weekendOnly
          ? t('playground.familyTicketsVsEventTimeDescWeekendOnly')
          : t('playground.familyTicketsVsEventTimeDescFull');

        return {
          id: `report-${Date.now()}`,
          type: reportType,
          title: t('playground.familyTicketsVsEventTime'),
          description: description,
          data: chartData,
          summary: weekendOnly 
            ? `${totalWeekendTickets} ${t('playground.weekendTicketsSummary')}`
            : `${totalWeekdayTickets} ${t('playground.weekdayTickets')}, ${totalWeekendTickets} ${t('playground.weekendTicketsSummary')}`
        };
      }

      case 'visitor_count': {
          // Filter for "Ekspozicij" events
          const exhibitionEvents = filteredRows.filter(row => {
            const renginys = row['Renginys'] || '';
            return renginys.toLowerCase().includes('ekspozicij');
          });

          // Group by weekday and calculate visitor count
          const weekdayGroups: { [key: string]: number } = {};
          
          exhibitionEvents.forEach(event => {
            const renginioData = event['Renginio data'] || '';
            const preke = getRowValue(event, ['Prekė', 'Preke']);
            
            if (!renginioData) return;
            
            const dateObj = parseDDMMYYYY(renginioData);
            if (!dateObj) return;
            
            const dayOfWeek = dateObj.getDay();
            const dayNames = [
              t('playground.sunday'),
              t('playground.monday'),
              t('playground.tuesday'),
              t('playground.wednesday'),
              t('playground.thursday'),
              t('playground.friday'),
              t('playground.saturday')
            ];
            const dayName = dayNames[dayOfWeek];
            
            // Convert to 1-7 numbering: Day 1=Monday, Day 2=Tuesday, ..., Day 7=Sunday
            const dayNumber = dayOfWeek === 0 ? 7 : dayOfWeek; // Sunday becomes 7
            
            // Apply weekend filter if enabled
            if (weekendOnly) {
              // Only include weekends (Saturday=6, Sunday=0/7)
              if (dayOfWeek !== 0 && dayOfWeek !== 6) return;
            } else {
              // Include all days Monday through Sunday (day 1-7)
              if (dayNumber < 1 || dayNumber > 7) return;
            }
            
            // Calculate visitor count with multiplier for family tickets
            let visitorCount = 1; // Default 1 visitor per ticket
            if (isFamilyTicketPreke(preke)) {
              visitorCount = 4.5; // Family tickets count as 4.5 visitors
            }
            
            if (!weekdayGroups[dayName]) {
              weekdayGroups[dayName] = 0;
            }
            weekdayGroups[dayName] += visitorCount;
          });

          // Create chart data in chronological order
          const dayOrder = weekendOnly 
            ? ['Saturday', 'Sunday']
            : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
          
          // Calculate total visitors BEFORE rounding per day (to avoid cumulative rounding errors)
          const totalRawVisitorsSum = dayOrder.reduce((sum, day) => sum + (weekdayGroups[day] || 0), 0);
          const totalVisitors = Math.round(totalRawVisitorsSum);
          
          const chartData = dayOrder
            .map(day => ({
              weekday: day,
              visitors: Math.round(weekdayGroups[day] || 0)
            }))
            .filter(item => item.visitors > 0); // Only show days with visitors


          const description = weekendOnly
            ? t('playground.visitorCountDescWeekendOnly')
            : t('playground.visitorCountDescFull');

          return {
            id: `report-${Date.now()}`,
            type: reportType,
            title: t('playground.numberOfVisitors'),
            description: description,
            data: chartData,
            summary: `${totalVisitors.toLocaleString()} ${t('playground.totalVisitorsSummary')}`
          };
        }

        case 'family_tickets_weekend_table': {
          // Filter for "Ekspozicijos Šeimos Bilietas" 
          const familyTickets = filteredRows.filter(row => {
            const preke = getRowValue(row, ['Prekė', 'Preke']);
            return isFamilyTicketPreke(preke);
          });

          // Group by event time and separate weekdays vs weekends
          const timeGroups: { [key: string]: { weekday: number; weekend: number } } = {};
          
          familyTickets.forEach(ticket => {
            const renginioLaikas = ticket['Renginio laikas'] || '';
            const renginioData = ticket['Renginio data'] || '';
            
            if (!renginioLaikas || !renginioData) return;
            
            const dateObj = parseDDMMYYYY(renginioData);
            if (!dateObj) return;
            
            const dayOfWeek = dateObj.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            
            // Apply weekend filter if enabled
            if (weekendOnly && !isWeekend) {
              return; // Skip non-weekend tickets when weekendOnly is true
            }
            
            if (!timeGroups[renginioLaikas]) {
              timeGroups[renginioLaikas] = { weekday: 0, weekend: 0 };
            }
            
            if (isWeekend) {
              timeGroups[renginioLaikas].weekend += 1;
            } else {
              timeGroups[renginioLaikas].weekday += 1;
            }
          });

          // Convert to table data and sort by time
          const tableData = Object.entries(timeGroups)
            .map(([time, counts]) => ({
              time,
              weekday: weekendOnly ? 0 : counts.weekday, // Hide weekday data when weekendOnly is true
              weekend: counts.weekend,
              total: (weekendOnly ? 0 : counts.weekday) + counts.weekend
            }))
            .sort((a, b) => {
              // Sort by time (assuming HH:MM format)
              const timeA = a.time.split(':').map(Number);
              const timeB = b.time.split(':').map(Number);
              return (timeA[0] * 60 + (timeA[1] || 0)) - (timeB[0] * 60 + (timeB[1] || 0));
            });

          const totalWeekdayTickets = tableData.reduce((sum, item) => sum + item.weekday, 0);
          const totalWeekendTickets = tableData.reduce((sum, item) => sum + item.weekend, 0);

          const description = weekendOnly
            ? t('playground.familyTicketsVsEventTimeTableDescWeekendOnly')
            : t('playground.familyTicketsVsEventTimeTableDesc');

          return {
            id: `report-${Date.now()}`,
            type: reportType,
            title: t('playground.familyTicketsVsEventTimeTable'),
            description: description,
            data: tableData,
            summary: weekendOnly 
              ? `${totalWeekendTickets} ${t('playground.weekendTicketsSummary')}`
              : `${totalWeekdayTickets} ${t('playground.weekdayTickets')}, ${totalWeekendTickets} ${t('playground.weekendTicketsSummary')}`
          };
        }

        case 'visitor_count_table': {
          // Filter for "Ekspozicij" events
          const exhibitionEvents = filteredRows.filter(row => {
            const renginys = row['Renginys'] || '';
            return renginys.toLowerCase().includes('ekspozicij');
          });

          // Group by weekday and calculate visitor count
          const weekdayGroups: { [key: string]: number } = {};
          
          exhibitionEvents.forEach(event => {
            const renginioData = event['Renginio data'] || '';
            const preke = getRowValue(event, ['Prekė', 'Preke']);
            
            if (!renginioData) return;
            
            const dateObj = parseDDMMYYYY(renginioData);
            if (!dateObj) return;
            
            const dayOfWeek = dateObj.getDay();
            const dayNames = [
              t('playground.sunday'),
              t('playground.monday'),
              t('playground.tuesday'),
              t('playground.wednesday'),
              t('playground.thursday'),
              t('playground.friday'),
              t('playground.saturday')
            ];
            const dayName = dayNames[dayOfWeek];
            
            // Convert to 1-7 numbering: Day 2=Tuesday, Day 3=Wednesday, Day 4=Thursday, Day 5=Friday, Day 6=Saturday, Day 7=Sunday
            const dayNumber = dayOfWeek === 0 ? 7 : dayOfWeek; // Sunday becomes 7
            
            // Apply weekend filter if enabled
            if (weekendOnly) {
              // Only include weekends (Saturday=6, Sunday=0/7)
              if (dayOfWeek !== 0 && dayOfWeek !== 6) return;
            } else {
              // Include all days Monday through Sunday (day 1-7)
              // Monday = 1, Tuesday = 2, ..., Sunday = 0 (converted to 7)
              if (dayNumber < 1 || dayNumber > 7) return;
            }
            
            // Calculate visitor count with multiplier for family tickets
            let visitorCount = 1; // Default 1 visitor per ticket
            if (isFamilyTicketPreke(preke)) {
              visitorCount = 4.5; // Family tickets count as 4.5 visitors
            }
            
            if (!weekdayGroups[dayName]) {
              weekdayGroups[dayName] = 0;
            }
            weekdayGroups[dayName] += visitorCount;
          });

          // Create table data in chronological order
          const dayOrder = weekendOnly 
            ? [t('playground.saturday'), t('playground.sunday')]
            : [t('playground.monday'), t('playground.tuesday'), t('playground.wednesday'), t('playground.thursday'), t('playground.friday'), t('playground.saturday'), t('playground.sunday')];
          
          // Calculate total visitors BEFORE rounding per day (to avoid cumulative rounding errors)
          const totalRawVisitorsSum = dayOrder.reduce((sum, day) => sum + (weekdayGroups[day] || 0), 0);
          const totalVisitors = Math.round(totalRawVisitorsSum);
          
          const tableData = dayOrder
            .map(day => ({
              weekday: day,
              visitors: Math.round(weekdayGroups[day] || 0),
              hasVisitors: (weekdayGroups[day] || 0) > 0
            }));

          const description = weekendOnly
            ? t('playground.visitorCountDescWeekendOnly')
            : t('playground.visitorCountDescFull');

          return {
            id: `report-${Date.now()}`,
            type: reportType,
            title: t('playground.numberOfVisitorsTable'),
            description: description,
            data: tableData,
            summary: `${totalVisitors.toLocaleString()} ${t('playground.totalVisitorsSummary')}`
          };
        }

        case 'savaitgaliai': {
          // Filter for "Ekspozicijos Šeimos Bilietas" tickets
          const familyTickets = filteredRows.filter(row => {
            const preke = getRowValue(row, ['Prekė', 'Preke']);
            return isFamilyTicketPreke(preke);
          });

          // Filter for weekend days only and group by date
          const weekendGroups: { [key: string]: { date: string; dayName: string; count: number } } = {};
          let skippedInvalidDates = 0;
          let skippedWeekdays = 0;
          
          familyTickets.forEach(ticket => {
            const renginioData = ticket['Renginio data'] || '';
            if (!renginioData) {
              skippedInvalidDates++;
              return;
            }
            
            const dateObj = parseDDMMYYYY(renginioData);
            if (!dateObj) {
              skippedInvalidDates++;
              return;
            }
            
            const dayOfWeek = dateObj.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
            
            if (!isWeekend) {
              skippedWeekdays++;
              return;
            }
            
            const dayName = dayOfWeek === 0 ? 'Sekmadienis' : 'Šeštadienis';
            if (!weekendGroups[renginioData]) {
              weekendGroups[renginioData] = {
                date: renginioData,
                dayName: dayName,
                count: 0
              };
            }
            weekendGroups[renginioData].count += 1;
          });

          // Convert to chart data and sort by date
          const chartData = Object.values(weekendGroups)
            .sort((a, b) => {
              const dateA = parseDDMMYYYY(a.date);
              const dateB = parseDDMMYYYY(b.date);
              if (!dateA || !dateB) return 0;
              return dateA.getTime() - dateB.getTime();
            });

          const totalTickets = chartData.reduce((sum, item) => sum + item.count, 0);

          return {
            id: `report-${Date.now()}`,
            type: reportType,
            title: t('playground.savaitgaliaiFamilyTickets'),
            description: `${t('playground.savaitgaliaiDesc')} (${totalTickets} ${t('common.total')})`,
            data: chartData,
            summary: `${totalTickets} ${t('playground.familyTicketsSold')} ${chartData.length} ${t('playground.weekendDays')}`
          };
        }

        case 'savaitgaliai_table': {
          // Filter for "Ekspozicijos Šeimos Bilietas" tickets
          const familyTickets = filteredRows.filter(row => {
            const preke = getRowValue(row, ['Prekė', 'Preke']);
            return isFamilyTicketPreke(preke);
          });

          // Filter for weekend days only and group by date
          const weekendGroups: { [key: string]: { date: string; dayName: string; count: number } } = {};
          
          familyTickets.forEach(ticket => {
            const renginioData = ticket['Renginio data'] || '';
            if (!renginioData) return;
            
            const dateObj = parseDDMMYYYY(renginioData);
            if (!dateObj) return;
            
            const dayOfWeek = dateObj.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
            
            if (isWeekend) {
              const dayName = dayOfWeek === 0 ? 'Sekmadienis' : 'Šeštadienis';
              if (!weekendGroups[renginioData]) {
                weekendGroups[renginioData] = {
                  date: renginioData,
                  dayName: dayName,
                  count: 0
                };
              }
              weekendGroups[renginioData].count += 1;
            }
          });

          // Convert to table data and sort by date
          const tableData = Object.values(weekendGroups)
            .sort((a, b) => {
              const dateA = parseDDMMYYYY(a.date);
              const dateB = parseDDMMYYYY(b.date);
              if (!dateA || !dateB) return 0;
              return dateA.getTime() - dateB.getTime();
            });

          const totalTickets = tableData.reduce((sum, item) => sum + item.count, 0);

          return {
            id: `report-${Date.now()}`,
            type: reportType,
            title: t('playground.savaitgaliaiFamilyTicketsTable'),
            description: `${t('playground.savaitgaliaiDesc')} (${totalTickets} ${t('common.total')})`,
            data: tableData,
            summary: `${totalTickets} ${t('playground.familyTicketsSold')} ${tableData.length} ${t('playground.weekendDays')}`
          };
        }

        case 'family_tickets_time_comparison': {
          // Filter for "Ekspozicijos Šeimos Bilietas" 
          const familyTickets = filteredRows.filter(row => {
            const preke = getRowValue(row, ['Prekė', 'Preke']);
            return isFamilyTicketPreke(preke);
          });

          // Group weekend tickets by event time
          const weekendTimeGroups: { [key: string]: number } = {};
          // Group all tickets (or weekend-only if checkbox checked) by event time
          const allTimeGroups: { [key: string]: number } = {};

          familyTickets.forEach(ticket => {
            const renginioLaikas = ticket['Renginio laikas'] || '';
            const renginioData = ticket['Renginio data'] || '';
            
            if (!renginioLaikas || !renginioData) return;
            
            const dateObj = parseDDMMYYYY(renginioData);
            if (!dateObj) return;
            
            const dayOfWeek = dateObj.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

            // Always add to weekend groups if it's a weekend
            if (isWeekend) {
              weekendTimeGroups[renginioLaikas] = (weekendTimeGroups[renginioLaikas] || 0) + 1;
            }

            // Add to all groups based on checkbox state
            if (weekendOnly) {
              // If checkbox checked, only add weekend tickets
              if (isWeekend) {
                allTimeGroups[renginioLaikas] = (allTimeGroups[renginioLaikas] || 0) + 1;
              }
            } else {
              // If checkbox unchecked, add all tickets
              allTimeGroups[renginioLaikas] = (allTimeGroups[renginioLaikas] || 0) + 1;
            }
          });

          // Convert to chart data and sort by time
          const sortByTime = (a: { time: string; count: number }, b: { time: string; count: number }) => {
            const timeA = a.time.split(':').map(Number);
            const timeB = b.time.split(':').map(Number);
            return (timeA[0] * 60 + (timeA[1] || 0)) - (timeB[0] * 60 + (timeB[1] || 0));
          };

          const weekendData = Object.entries(weekendTimeGroups)
            .map(([time, count]) => ({ time, count }))
            .sort(sortByTime);

          const allData = Object.entries(allTimeGroups)
            .map(([time, count]) => ({ time, count }))
            .sort(sortByTime);

          // Get all unique times to ensure both lines have same x-axis scale
          const allTimes = new Set([...weekendData.map(d => d.time), ...allData.map(d => d.time)]);
          const sortedTimes = Array.from(allTimes).sort((a, b) => {
            const timeA = a.split(':').map(Number);
            const timeB = b.split(':').map(Number);
            return (timeA[0] * 60 + (timeA[1] || 0)) - (timeB[0] * 60 + (timeB[1] || 0));
          });

          // Combine data into single array with both weekend and all counts
          const combinedData = sortedTimes.map(time => {
            const weekendItem = weekendData.find(d => d.time === time);
            const allItem = allData.find(d => d.time === time);
            return {
              time,
              weekend: weekendItem ? weekendItem.count : 0,
              all: allItem ? allItem.count : 0
            };
          });

          const totalWeekendTickets = weekendData.reduce((sum, item) => sum + item.count, 0);
          const totalAllTickets = allData.reduce((sum, item) => sum + item.count, 0);

          return {
            id: `report-${Date.now()}`,
            type: reportType,
            title: t('playground.familyTicketsTimeComparison'),
            description: weekendOnly 
              ? t('playground.familyTicketsTimeComparisonDescWeekendOnly')
              : t('playground.familyTicketsTimeComparisonDesc'),
            data: combinedData,
            summary: `${t('playground.weekendTicketsLabel')}: ${totalWeekendTickets} ${t('playground.ticketCount')} | ${t('playground.allTicketsLabel')}: ${totalAllTickets} ${t('playground.ticketCount')}`
          };
        }

        case 'coupon_sales_daily': {
          // Check if date range is selected - if not, include year in date formatting
          const includeYear = !dateStart && !dateEnd;

          // Filter rows by "Mokėjimo laikas" date range
          const couponRows = csvData.rows.filter(row => {
            const mokejimoLaikas = row['Mokėjimo laikas'] || '';
            if (!mokejimoLaikas) return false;
            const dateObj = parseDDMMYYYYHHMMSS(mokejimoLaikas);
            if (!dateObj) return false;
            return isInRange(dateObj, dateStart, dateEnd);
          });

          // Group by date - calculate total quantity and total revenue per day
          interface DateGroup {
            dateObj: Date;
            totalQuantity: number;
            totalRevenue: number;
          }
          const dateGroups: { [key: string]: DateGroup } = {};

          couponRows.forEach(row => {
            const mokejimoLaikas = row['Mokėjimo laikas'] || '';
            const kiekis = parseFloat(row['Kiekis'] || '0');
            const kainaSuNuolaida = parseFloat(row['Kaina su nuolaida'] || row['Kaina su nuolaida.'] || '0');

            if (!mokejimoLaikas || isNaN(kiekis) || isNaN(kainaSuNuolaida)) return;

            const dateObj = parseDDMMYYYYHHMMSS(mokejimoLaikas);
            if (!dateObj) return;

            const dateKey = formatMonthDay(dateObj, includeYear);

            if (!dateGroups[dateKey]) {
              dateGroups[dateKey] = { dateObj, totalQuantity: 0, totalRevenue: 0 };
            }

            dateGroups[dateKey].totalQuantity += kiekis;
            dateGroups[dateKey].totalRevenue += kainaSuNuolaida * kiekis;
          });

          // Convert to chart data format and sort chronologically
          const chartData = Object.entries(dateGroups)
            .map(([date, group]) => ({
              date,
              quantity: group.totalQuantity,
              revenue: group.totalRevenue,
              dateObj: group.dateObj
            }))
            .sort((a, b) => {
              // Sort chronologically by date object
              return a.dateObj.getTime() - b.dateObj.getTime();
            })
            .map(({ dateObj, ...rest }) => rest); // Remove dateObj from final data

          const totalQuantity = chartData.reduce((sum, item) => sum + item.quantity, 0);
          const totalRevenue = chartData.reduce((sum, item) => sum + item.revenue, 0);

          return {
            id: `report-${Date.now()}`,
            type: reportType,
            title: t('playground.couponSalesDaily'),
            description: t('playground.couponSalesDailyDesc'),
            data: chartData,
            summary: `${t('playground.totalQuantity')}: ${totalQuantity.toFixed(0)} | ${t('playground.totalRevenue')}: ${totalRevenue.toFixed(2)}`
          };
        }

        default:
          return null;
      }
    }, [csvData, t]);

  // Add new report
  const addReport = useCallback(() => {
    if (!csvData) return;

    const newReport = generateReport(selectedReportType, startDate, endDate, weekendOnly);
    if (newReport) {
      setReports(prev => [...prev, newReport]);
    }
  }, [csvData, selectedReportType, startDate, endDate, weekendOnly, generateReport]);

  // Remove report
  const removeReport = useCallback((reportId: string) => {
    setReports(prev => prev.filter(report => report.id !== reportId));
  }, []);

  // Render report content
  const renderReportContent = useCallback((report: ReportConfig) => {
    const { type, data, title, description, summary } = report;

    return (
      <>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: 'rgba(0,0,0,0.87)' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, color: 'rgba(0,0,0,0.6)' }}>
          {description}
        </Typography>
        {summary && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center', color: 'rgba(0,0,0,0.87)' }}>
              {summary}
            </Typography>
          </Box>
        )}
        
        {/* Chart Display */}
        <Box sx={{ mt: 3, height: 'auto', minHeight: '400px' }}>
          {type === 'family_tickets_weekend' && (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={Array.isArray(data) ? data : []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.2)" />
                <XAxis 
                  dataKey="time" 
                  stroke="rgba(0,0,0,0.8)"
                  tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                />
                <YAxis 
                  stroke="rgba(0,0,0,0.8)"
                  tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255,255,255,0.95)', 
                    border: '1px solid rgba(0,0,0,0.2)',
                    borderRadius: 8,
                    color: 'rgba(0,0,0,0.8)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="weekday" 
                  stroke="#4ECDC4" 
                  strokeWidth={2}
                  name={t('playground.weekday')}
                />
                <Line 
                  type="monotone" 
                  dataKey="weekend" 
                  stroke="#FF6B6B" 
                  strokeWidth={2}
                  name={t('playground.weekend')}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
          
          {type === 'visitor_count' && (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={Array.isArray(data) ? data : []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.2)" />
                <XAxis 
                  dataKey="weekday" 
                  stroke="rgba(0,0,0,0.8)"
                  tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                />
                <YAxis 
                  stroke="rgba(0,0,0,0.8)"
                  tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255,255,255,0.95)', 
                    border: '1px solid rgba(0,0,0,0.2)',
                    borderRadius: 8,
                    color: 'rgba(0,0,0,0.8)'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="visitors" 
                  fill="#45B7D1" 
                  radius={[4, 4, 0, 0]}
                  stroke="#3A9BC7"
                  strokeWidth={2}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
          
          {type === 'savaitgaliai' && (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={Array.isArray(data) ? data : []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.2)" />
                <XAxis 
                  dataKey="date" 
                  stroke="rgba(0,0,0,0.8)"
                  tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis 
                  stroke="rgba(0,0,0,0.8)"
                  tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255,255,255,0.95)', 
                    border: '1px solid rgba(0,0,0,0.2)',
                    borderRadius: 8,
                    color: 'rgba(0,0,0,0.8)'
                  }}
                  formatter={(value: any, name: string, props: any) => [
                    `${value} bilietų (${props.payload.dayName})`,
                    'Kiekis'
                  ]}
                />
                <Legend />
                <Bar 
                  dataKey="count" 
                  fill="#FF6B6B" 
                  radius={[4, 4, 0, 0]}
                  stroke="#E55555"
                  strokeWidth={2}
                />
              </BarChart>
            </ResponsiveContainer>
          )}

          {type === 'family_tickets_time_comparison' && (
            <Box sx={{ mt: 3 }}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={Array.isArray(data) ? data : []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.2)" />
                  <XAxis 
                    dataKey="time" 
                    stroke="rgba(0,0,0,0.8)"
                    tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    stroke="rgba(0,0,0,0.8)"
                    tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                    label={{ value: t('playground.ticketCount'), angle: -90, position: 'insideLeft' }}
                  />
                  <RechartsTooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255,255,255,0.95)', 
                      border: '1px solid rgba(0,0,0,0.2)',
                      borderRadius: 8,
                      color: 'rgba(0,0,0,0.8)'
                    }}
                    formatter={(value: any) => [`${value} tickets`, 'Count']}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="weekend" 
                    stroke="#4ECDC4" 
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name={t('playground.weekendTickets')}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="all" 
                    stroke="#FF6B6B" 
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name={t('playground.allTickets')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}

          {type === 'coupon_sales_daily' && Array.isArray(data) && data.length > 0 && (
            <Box sx={{ mt: 3, width: '100%' }}>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.2)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(0,0,0,0.8)"
                    tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis 
                    yAxisId="left"
                    stroke="rgba(0,0,0,0.8)"
                    tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                    label={{ value: t('playground.quantity'), angle: -90, position: 'insideLeft' }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    stroke="rgba(0,0,0,0.8)"
                    tick={{ fill: 'rgba(0,0,0,0.8)', fontSize: 12 }}
                    label={{ value: t('playground.revenue'), angle: 90, position: 'insideRight' }}
                  />
                  <RechartsTooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255,255,255,0.95)', 
                      border: '1px solid rgba(0,0,0,0.2)',
                      borderRadius: 8,
                      color: 'rgba(0,0,0,0.8)'
                    }}
                  />
                  <Legend />
                  {/* Unified bar for total quantity per day */}
                  <Bar
                    yAxisId="left"
                    dataKey="quantity"
                    fill="#45B7D1"
                    radius={[4, 4, 0, 0]}
                    stroke="#3A9BC7"
                    strokeWidth={2}
                    name={t('playground.totalQuantity')}
                  />
                  {/* Line for total revenue per day */}
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#FF6B6B" 
                    strokeWidth={3}
                    dot={{ r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('playground.totalRevenue')}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Table Reports */}
          {(type === 'family_tickets_weekend_table' || type === 'visitor_count_table' || type === 'savaitgaliai_table') && (
            <Box sx={{ mt: 3 }}>
              <Paper sx={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)' }}>
                <Box sx={{ overflow: 'visible' }}>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: type === 'family_tickets_weekend_table' ? '1fr 1fr 1fr 1fr' : 
                                     type === 'savaitgaliai_table' ? '1fr 1fr 1fr' : '1fr 1fr 1fr',
                    bgcolor: 'rgba(0,0,0,0.1)',
                    borderBottom: '2px solid rgba(0,0,0,0.3)',
                    fontWeight: 'bold',
                    color: 'rgba(0,0,0,0.87)',
                    fontSize: '0.875rem'
                  }}>
                    {type === 'family_tickets_weekend_table' && (
                      <>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.eventTimeHeader')}</Box>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.weekdayHeader')}</Box>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.weekendHeader')}</Box>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.totalHeader')}</Box>
                      </>
                    )}
                    {type === 'visitor_count_table' && (
                      <>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.weekdayHeader')}</Box>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.visitorsHeader')}</Box>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.statusHeader')}</Box>
                      </>
                    )}
                    {type === 'savaitgaliai_table' && (
                      <>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.dateHeader')}</Box>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.dayOfWeekHeader')}</Box>
                        <Box sx={{ p: 1, textAlign: 'center' }}>{t('playground.ticketCountHeader')}</Box>
                      </>
                    )}
                  </Box>
                  {(Array.isArray(data) ? data : []).map((row: any, index: number) => (
                    <Box key={index} sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: type === 'family_tickets_weekend_table' ? '1fr 1fr 1fr 1fr' : 
                                       type === 'savaitgaliai_table' ? '1fr 1fr 1fr' : '1fr 1fr 1fr',
                      borderBottom: '1px solid rgba(0,0,0,0.1)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
                    }}>
                      {type === 'family_tickets_weekend_table' && (
                        <>
                          <Box sx={{ p: 1, textAlign: 'center', color: 'rgba(0,0,0,0.87)', fontSize: '0.875rem' }}>{row.time}</Box>
                          <Box sx={{ p: 1, textAlign: 'center', color: 'rgba(0,0,0,0.87)', fontSize: '0.875rem' }}>{row.weekday}</Box>
                          <Box sx={{ p: 1, textAlign: 'center', color: 'rgba(0,0,0,0.87)', fontSize: '0.875rem' }}>{row.weekend}</Box>
                          <Box sx={{ p: 1, textAlign: 'center', color: 'rgba(0,0,0,0.87)', fontWeight: 'bold', fontSize: '0.875rem' }}>{row.total}</Box>
                        </>
                      )}
                      {type === 'visitor_count_table' && (
                        <>
                          <Box sx={{ p: 1, textAlign: 'center', color: 'rgba(0,0,0,0.87)', fontSize: '0.875rem' }}>{row.weekday}</Box>
                          <Box sx={{ p: 1, textAlign: 'center', color: 'rgba(0,0,0,0.87)', fontSize: '0.875rem' }}>{row.visitors}</Box>
                          <Box sx={{ p: 1, textAlign: 'center' }}>
                            <Chip 
                              label={row.hasVisitors ? t('playground.active') : t('playground.noVisitors')} 
                              size="small"
                              color={row.hasVisitors ? 'success' : 'default'}
                              sx={{ fontSize: '0.75rem' }}
                            />
                          </Box>
                        </>
                      )}
                      {type === 'savaitgaliai_table' && (
                        <>
                          <Box sx={{ p: 1, textAlign: 'center', color: 'rgba(0,0,0,0.87)', fontSize: '0.875rem' }}>{row.date}</Box>
                          <Box sx={{ p: 1, textAlign: 'center', color: 'rgba(0,0,0,0.87)', fontSize: '0.875rem' }}>{row.dayName}</Box>
                          <Box sx={{ p: 1, textAlign: 'center', color: 'rgba(0,0,0,0.87)', fontWeight: 'bold', fontSize: '0.875rem' }}>{row.count}</Box>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      </>
    );
  }, []);

  // Early return for non-master admins
  if (!isMasterAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. This panel is only available for administrators.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)` }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ 
          fontWeight: 'bold', 
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1
        }}>
          🎮 {t('playground.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('playground.subtitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('playground.currentUser')}: {user?.email}
        </Typography>
      </Box>

      {/* File Upload Section */}
      <Card sx={{ mb: 4, background: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(10px)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <AnalyticsIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Typography variant="h5" component="h2">
              {t('playground.dataSource')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                }
              }}
            >
              {t('playground.uploadCsvFile')} / Excel
              <input
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
              />
            </Button>
            
            {csvData && (
              <Chip 
                label={`${csvData.rows.length} rows, ${csvData.headers.length} columns`}
                color="success"
                icon={<GridIcon />}
              />
            )}
            
            {isLoading && <CircularProgress size={24} />}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Chart Controls */}
      {csvData && (
        <Card sx={{ mb: 4, background: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(10px)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <PaletteIcon sx={{ fontSize: 32, color: theme.palette.secondary.main }} />
                          <Typography variant="h5" component="h2">
              Report Controls
            </Typography>
            </Box>
            
            {/* Data Summary */}
            <Box sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
              <Typography variant="subtitle2" color="info.main" sx={{ mb: 1 }}>
                📊 Data Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {csvData.rows.length.toLocaleString()} records • {csvData.headers.length} columns
                {csvData.headers.includes('Sektorius') && ' • Exhibition Data Detected'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
              <Box sx={{ minWidth: 200, flex: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('playground.reportType')}</InputLabel>
                  <Select
                    value={selectedReportType}
                    label={t('playground.reportType')}
                    onChange={(e) => setSelectedReportType(e.target.value as ReportType)}
                  >
                                                                          <MenuItem value="family_tickets_weekend">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AnalyticsIcon /> {t('playground.familyTicketsVsEventTime')}
                  </Box>
                </MenuItem>
                <MenuItem value="visitor_count">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon /> {t('playground.numberOfVisitors')}
                  </Box>
                </MenuItem>
                <MenuItem value="family_tickets_weekend_table">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GridIcon /> {t('playground.familyTicketsVsEventTimeTable')}
                  </Box>
                </MenuItem>
                <MenuItem value="visitor_count_table">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GridIcon /> {t('playground.numberOfVisitorsTable')}
                  </Box>
                </MenuItem>
                <MenuItem value="savaitgaliai">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon /> {t('playground.savaitgaliaiFamilyTickets')}
                  </Box>
                </MenuItem>
                <MenuItem value="savaitgaliai_table">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GridIcon /> {t('playground.savaitgaliaiFamilyTicketsTable')}
                  </Box>
                </MenuItem>
                <MenuItem value="family_tickets_time_comparison">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LineChartIcon /> {t('playground.familyTicketsTimeComparison')}
                  </Box>
                </MenuItem>
                <MenuItem value="coupon_sales_daily">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon /> {t('playground.couponSalesDaily')}
                  </Box>
                </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Date Range Filters */}
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <DatePicker
                    label={t('playground.fromDate')}
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { minWidth: 150 }
                      }
                    }}
                  />
                  <DatePicker
                    label={t('playground.toDate')}
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { minWidth: 150 }
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setStartDate(null);
                      setEndDate(null);
                    }}
                    startIcon={<ClearIcon />}
                    sx={{ minWidth: 100 }}
                    title={t('playground.clearDates')}
                  >
                    {t('playground.clear')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handlePresetClick}
                    sx={{ minWidth: 120 }}
                  >
                    {t('playground.presets')}
                  </Button>
                  <Menu
                    anchorEl={presetMenuAnchor}
                    open={Boolean(presetMenuAnchor)}
                    onClose={handlePresetClose}
                  >
                    <MenuItem onClick={() => applyPreset('thisWeek')}>{t('playground.thisWeek')}</MenuItem>
                    <MenuItem onClick={() => applyPreset('lastWeek')}>{t('playground.lastWeek')}</MenuItem>
                    <MenuItem onClick={() => applyPreset('thisMonth')}>{t('playground.thisMonth')}</MenuItem>
                    <MenuItem onClick={() => applyPreset('lastMonth')}>{t('playground.lastMonth')}</MenuItem>
                    <MenuItem onClick={() => applyPreset('thisQuarter')}>{t('playground.thisQuarter')}</MenuItem>
                    <MenuItem onClick={() => applyPreset('lastQuarter')}>{t('playground.lastQuarter')}</MenuItem>
                    <MenuItem onClick={() => applyPreset('thisYear')}>{t('playground.thisYear')}</MenuItem>
                    <MenuItem onClick={() => applyPreset('lastYear')}>{t('playground.lastYear')}</MenuItem>
                  </Menu>
                </Box>
              </LocalizationProvider>
              <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mt: -2 }}>
                Palikite tuščią, kad būtų įtrauktos visos datos
              </Typography>
              
              {/* Weekend Only Checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={weekendOnly}
                    onChange={(e) => setWeekendOnly(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('playground.onlyWeekends')}
                sx={{ width: '100%' }}
              />
              
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={addReport}
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                  }
                }}
              >
                Generate Report
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setReports([])}
              >
                Clear All
              </Button>
            </Box>
            

          </CardContent>
        </Card>
      )}

             {/* Reports Grid */}
       {reports.length > 0 && (
          <Box sx={{ 
            columnCount: { xs: 1, sm: 2, md: 3 },
            columnGap: 3,
            '& > *': { 
              breakInside: 'avoid',
              marginBottom: 3,
              display: 'block'
            }
          }}>
           {reports.map((report) => (
             <Card key={report.id} sx={{ 
               background: alpha(theme.palette.background.paper, 0.9), 
               backdropFilter: 'blur(10px)',
               border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
               transition: 'all 0.3s ease',
               // Make coupon_sales_daily report span full width (3 columns)
               ...(report.type === 'coupon_sales_daily' && {
                 columnSpan: 'all',
                 width: '100%'
               }),
               '&:hover': {
                 transform: 'translateY(-4px)',
                 boxShadow: theme.shadows[8],
                 border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
               }
             }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', color: 'rgba(0,0,0,0.87)' }}>
                    {report.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={t('playground.reportSettings')}>
                      <IconButton size="small">
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove Report">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => removeReport(report.id)}
                      >
                        ×
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                
                                 <Divider sx={{ mb: 2 }} />
                 
                 {renderReportContent(report)}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Empty State */}
      {!csvData && (
        <Card sx={{ 
          textAlign: 'center', 
          py: 8, 
          background: alpha(theme.palette.background.paper, 0.9), 
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent>
            <AnalyticsIcon sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2 }} />
            <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
              {t('playground.readyToGenerate')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('playground.uploadDescription')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              component="label"
              startIcon={<UploadIcon />}
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                }
              }}
            >
              Pasirinkti failą
              <input
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
              />
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Playground; 